/**
 * Limite "Até dois" por fileira (banco de trás / 3ª fileira).
 *
 * Módulo PURO de propósito — o mesmo raciocínio de `lib/seat-pricing.ts` e
 * `lib/edit-ride-rules.ts`: o jest roda em `testEnvironment: node` e só coleta
 * `__tests__/*.test.ts`, então regra que mora dentro do JSX não tem como ser
 * testada. Nada aqui toca Mongo, Next ou MercadoPago.
 *
 * O que o limite significa: a fileira comporta NO MÁXIMO 2 pessoas. Não é preço,
 * não é prêmio e não tem nada a ver com o Conforto Traseiro (que é um mecanismo
 * paralelo, ligado pelo passageiro, e que também bloqueia o banco do meio).
 *
 * O efeito é MATERIALIZADO no seatMap (o banco do meio vira `available:false`),
 * e não calculado na leitura. É o que garante que TODA checagem de
 * disponibilidade já existente (`seatAvailableForTrecho`, `seatAvailableForKm`,
 * o re-check do pagamento, a busca) bloqueie o assento sem precisar saber que
 * essa feature existe.
 *
 * O estado guardado é por fileira (`rowCaps[rowKey]`) porque o painel precisa
 * distinguir três coisas que o seatMap sozinho não distingue:
 *   - o meio está bloqueado porque o MOTORISTA mandou bloquear;
 *   - o meio está bloqueado porque o CONFORTO segurou (`comfortHold`);
 *   - o meio está bloqueado porque o LIMITE segurou.
 * Sem isso, desligar o limite viraria "devolve o meio" e ressuscitaria um
 * assento que o motorista desativou de propósito.
 */

/** Estado do limite de uma fileira. */
export interface RowCap {
  /** "Até dois" LIGADO nesta fileira. */
  maxTwo: boolean
  /** O limite é quem segura o banco do meio (`available:false` + este flag). */
  holdsMiddle: boolean
}

/** Limites por fileira — a chave é o `rowKey` ("1" = banco de trás, "2" = 3ª). */
export type RowCaps = Record<string, RowCap>

/** Assento lido pelas regras — estrutural, para não acoplar o módulo ao types. */
interface SeatLike {
  id: string
  row: number
  side: string
  available: boolean
  /** Segurado pelo Conforto Traseiro (`comfortHold` + `available:false`). */
  comfortHold?: boolean
}

/** Aviso mostrado quando o motorista só liberou 2 assentos na fileira. */
export const ROW_CAP_WARNING_TWO_SEATS =
  "Você disponibilizou apenas 2 assentos nesta fileira. Esta opção exige que até 2 pessoas ocupem a fileira de assentos."

/** Motivo do toggle travado: o Conforto Traseiro já é dono do banco do meio. */
export const ROW_CAP_COMFORT_CONFLICT =
  "O Conforto Traseiro já segura o banco do meio desta fileira."

/**
 * Motivo do toggle travado: já existe reserva ativa no banco do meio.
 *
 * Bloquear um assento reservado quebraria a reserva — a API rejeita o PUT com
 * "Não é possível indisponibilizar um assento já reservado".
 */
export const ROW_CAP_RESERVED_CONFLICT =
  "Já existe uma reserva ativa no banco do meio desta fileira."

/** Rótulo do toggle na Etapa 8. */
export function rowCapLabel(row: number): string {
  if (row === 1) return "Até dois no banco de trás"
  if (row === 2) return "Até dois na terceira fileira"
  return `Até dois na fileira ${row}`
}

/** Rótulo curto — usado no badge do passageiro. */
export function rowCapShortLabel(row: number): string {
  if (row === 1) return "Banco de trás"
  if (row === 2) return "3ª fileira"
  return `Fileira ${row}`
}

/**
 * Lê o `rowCaps` vindo do banco (ou de um body) tolerando dado sujo.
 *
 * Só interessa fileira com `maxTwo: true`: o driver desliga o limite apagando
 * a chave, então um `maxTwo: false` gravado é lixo e não pode ressuscitar
 * nada.
 *
 * `holdsMiddle` é COERCIDO para booleano (`=== true`): um valor sujo no banco
 * vira `false` (nada segurado) em vez de vazar para a lógica do limite.
 */
export function normalizeRowCaps(raw: unknown): RowCaps {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {}
  const out: RowCaps = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!/^\d+$/.test(key)) continue
    if (!value || typeof value !== "object" || Array.isArray(value)) continue
    const cap = value as Record<string, unknown>
    if (cap.maxTwo !== true) continue
    out[key] = { maxTwo: true, holdsMiddle: cap.holdsMiddle === true }
  }
  return out
}

function rowSeats(seats: ReadonlyArray<SeatLike>, row: number): SeatLike[] {
  return seats.filter((s) => s.row === row)
}

function middleOf(row: ReadonlyArray<SeatLike>): SeatLike | undefined {
  return row.find((s) => s.side === "center")
}

/**
 * Quantas vagas o MOTORISTA ofereceu na fileira — a base, antes do limite.
 *
 * A base NÃO é a contagem de assentos livres agora: o banco do meio pode estar
 * bloqueado pelo próprio limite ou pelo Conforto, e nesses casos o motorista
 * CONTINUA tendo oferecido aquela vaga. Sem essa distinção o painel diria
 * "você liberou só 2 assentos" para quem ofereceu 3 e o Conforto segurou o
 * meio.
 */
export function rowBaseFreeCount(
  seats: ReadonlyArray<SeatLike>,
  row: number,
  caps: RowCaps
): number {
  const cap = caps[String(row)]
  const list = rowSeats(seats, row)
  const windows = list.filter((s) => s.side !== "center")
  let count = windows.filter((s) => s.available === true).length
  const mid = middleOf(list)
  if (mid) {
    const offered =
      mid.available === true ||
      mid.comfortHold === true ||
      (cap?.maxTwo === true && cap.holdsMiddle === true)
    if (offered) count += 1
  }
  return count
}

/** Estado do limite de uma fileira, como a UI precisa enxergar. */
export interface RowCapState {
  /** O toggle aparece na Etapa 8. */
  visible: boolean
  /** O limite está ligado. */
  active: boolean
  /** O limite é quem segura o banco do meio. */
  holdsMiddle: boolean
  /** Vagas que o motorista ofereceu (antes do limite e do Conforto). */
  baseFreeCount: number
  /** Vagas livres AGORA (depois do limite e do Conforto). */
  effectiveFreeCount: number
  /** O Conforto Traseiro é dono do banco do meio desta fileira. */
  comfortOwnsMiddle: boolean
  /** Já existe reserva ativa no banco do meio desta fileira. */
  middleReserved: boolean
  /**
   * O limite não pode ser LIGADO agora (o Conforto é o dono do meio, ou o
   * meio já está reservado). DESLIGAR é sempre possível — travar os dois
   * sentidos deixaria o motorista sem saída. Por isso, quando o limite já
   * está ligado, o toggle NUNCA fica travado.
   */
  disabled: boolean
  disabledReason: string | null
  /** Aviso de "você só liberou 2 assentos nesta fileira". */
  warning: string | null
}

/**
 * Estado do limite de uma fileira.
 *
 * O toggle só aparece quando a fileira TEM banco do meio e o motorista ofereceu
 * 2 ou 3 vagas — oferecer 1 não dá para "limitar a 2" e oferecer 0 é um caso
 * sem sentido. Um limite já ligado continua visível mesmo se a base cair
 * (o motorista desativou uma janela na Etapa 3): esconder o painel deixaria
 * o limite ligado sem nenhuma forma de desligá-lo.
 */
export function rowCapState(
  seats: ReadonlyArray<SeatLike>,
  row: number,
  caps: RowCaps,
  reservedSeatIds?: ReadonlySet<string>
): RowCapState {
  const cap = caps[String(row)]
  const active = cap?.maxTwo === true
  const list = rowSeats(seats, row)
  const mid = middleOf(list)
  const comfortOwnsMiddle = mid?.comfortHold === true
  const middleReserved = !!mid && !!reservedSeatIds?.has(mid.id)
  const baseFreeCount = rowBaseFreeCount(seats, row, caps)
  const effectiveFreeCount = list.filter((s) => s.available === true).length
  return {
    visible: !!mid && (baseFreeCount >= 2 || active),
    active,
    holdsMiddle: active && cap?.holdsMiddle === true,
    baseFreeCount,
    effectiveFreeCount,
    comfortOwnsMiddle,
    middleReserved,
    disabled: !active && (comfortOwnsMiddle || middleReserved),
    disabledReason: comfortOwnsMiddle
      ? ROW_CAP_COMFORT_CONFLICT
      : middleReserved
        ? ROW_CAP_RESERVED_CONFLICT
        : null,
    warning: baseFreeCount === 2 ? ROW_CAP_WARNING_TWO_SEATS : null,
  }
}

/**
 * Liga/desliga o limite de uma fileira. Puro: devolve os novos assentos e os
 * novos limites, sem mutar o que recebeu.
 *
 * LIGAR: só bloqueia o banco do meio quando o motorista ofereceu 3 vagas (base
 * 3) — com 2 vagas o limite já é respeitado e nada muda no mapa (a UI avisa
 * com `ROW_CAP_WARNING_TWO_SEATS`). O bloqueio é do MEIO, nunca de uma janela:
 * a fileira continua com as duas janelas do motorista.
 *
 * A base 3 com o banco do meio LIVRE é o único caso que bloqueia. Se o meio
 * estiver livre mas uma janela estiver desligada, a base é 2 e o limite não
 * degenera a fileira para 1 lugar.
 *
 * DESLIGAR: devolve o banco do meio SE (e só se) o limite é quem segurava.
 * Um meio bloqueado pelo Conforto ou pelo próprio motorista não é tocado.
 */
export function toggleRowCap<T extends SeatLike>(
  seats: T[],
  caps: RowCaps,
  row: number,
  reservedSeatIds?: ReadonlySet<string>
): { seats: T[]; caps: RowCaps } {
  const rowKey = String(row)
  const cap = caps[rowKey]
  const list = rowSeats(seats, row)
  const mid = middleOf(list)

  if (cap?.maxTwo === true) {
    const nextCaps: RowCaps = { ...caps }
    delete nextCaps[rowKey]
    if (!cap.holdsMiddle || !mid || mid.comfortHold === true) {
      return { seats, caps: nextCaps }
    }
    return {
      seats: seats.map((s) => (s.id === mid.id ? { ...s, available: true } : s)),
      caps: nextCaps,
    }
  }

  // O Conforto Traseiro é dono do banco do meio: o limite não disputa o meio
  // com ele (o Conforto pode soltar o banco numa quebra e o "até dois"
  // viraria mentira).
  if (mid?.comfortHold === true) {
    return { seats, caps }
  }

  // Reserva ativa no banco do meio: bloquear o assento quebraria a reserva
  // (a API rejeita o PUT). O toggle já chega travado na UI — aqui é a rede de
  // segurança para quem chamar o módulo direto.
  if (mid && reservedSeatIds?.has(mid.id)) {
    return { seats, caps }
  }

  const baseFreeCount = rowBaseFreeCount(seats, row, caps)
  const nextCaps: RowCaps = { ...caps, [rowKey]: { maxTwo: true, holdsMiddle: false } }
  // Só segura o banco do meio quando a fileira tem MAIS de 2 lugares
  // oferecidos (3 na prática: 2 janelas + meio). Com 2 o limite já é respeitado
  // e nada muda no mapa (a UI avisa com `ROW_CAP_WARNING_TWO_SEATS`).
  if (baseFreeCount <= 2 || !mid) {
    return { seats, caps: nextCaps }
  }
  nextCaps[rowKey] = { maxTwo: true, holdsMiddle: true }
  return {
    seats: seats.map((s) => (s.id === mid.id ? { ...s, available: false } : s)),
    caps: nextCaps,
  }
}

/**
 * Reconcilia o limite com uma edição manual dos assentos (Etapa 3).
 *
 * O limite é uma REGRA, não um bloqueio cego: com ele ligado a fileira fica
 * com NO MÁXIMO 2 lugares. Depois de qualquer edição manual o limite é
 * re-materializado, senão a promessa vira mentira:
 *   - base 3 → o limite segura o banco do meio (2 lugares);
 *   - base 2 → o limite não precisa segurar nada (2 lugares) e DEVOLVE o
 *     banco do meio que ele segurava — o motorista não perde lugar por ter
 *     ligado o limite;
 *   - base 1 → o limite é inútil (1 lugar) e fica ligado só para o motorista
 *     poder desligá-lo.
 *
 * O limite SAI DE CENA quando não consegue HONRAR a promessa — a fileira
 * ficaria com 3 lugares e o "até dois" (que o passageiro vê no badge) viraria
 * mentira. Promessa retirada é honesta, promessa quebrada não é:
 *   - o CONFORTO é o dono do banco do meio: o Conforto pode soltá-lo a
 *     qualquer momento (recusa, prazo, meio já vendido) e a fileira voltaria
 *     a ter 3 lugares com o limite ligado;
 *   - o banco do meio já está RESERVADO: segurá-lo quebraria a reserva — a API
 *     rejeita o PUT com "Não é possível indisponibilizar um assento já
 *     reservado" e o PUT inteiro (descrição, preço, rota) seria perdido.
 *
 * Se o motorista mexeu no banco do meio À MÃO, o limite sai de cena: o clique
 * é o gesto de "quero o banco do meio de volta" e não pode ficar preso atrás
 * de um painel que ninguém vê. Desativar uma JANELA não tira o limite de cena.
 */
export function syncRowCapsWithSeatEdits<T extends SeatLike>(
  prevSeats: ReadonlyArray<SeatLike>,
  nextSeats: T[],
  caps: RowCaps,
  reservedSeatIds?: ReadonlySet<string>
): { seats: T[]; caps: RowCaps } {
  let seats = nextSeats
  let outCaps: RowCaps | null = null
  // Só cria o objeto novo quando algo MUDA (identidade preservada = o React
  // não re-renderiza à toa).
  const out = (): RowCaps => (outCaps ??= { ...caps })

  for (const [rowKey, cap] of Object.entries(caps)) {
    if (cap.maxTwo !== true) continue
    const row = Number(rowKey)
    const prevMid = middleOf(rowSeats(prevSeats, row))
    const nextMid = middleOf(rowSeats(seats, row))
    if (!nextMid) continue

    // O motorista mexeu no banco do meio à mão → o limite sai de cena.
    if (prevMid && prevMid.available !== nextMid.available) {
      delete out()[rowKey]
      continue
    }

    const base = rowBaseFreeCount(seats, row, outCaps ?? caps)
    const comfortOwns = nextMid.comfortHold === true
    const middleReserved = reservedSeatIds?.has(nextMid.id) === true
    // Segurar o banco do meio é a ÚNICA forma de caber 3 vagas oferecidas
    // dentro de 2 lugares.
    const canHoldMiddle = base >= 3 && !comfortOwns && !middleReserved

    if (base >= 3 && !canHoldMiddle) {
      // Não dá para segurar o meio (o Conforto é o dono, ou o meio já está
      // reservado) e a fileira iria vender 3 lugares com o limite ligado:
      // o limite SAI DE CENA em vez de virar promessa mentirosa.
      delete out()[rowKey]
      if (cap.holdsMiddle && !comfortOwns && !middleReserved) {
        // Devolve o banco do meio que o LIMITE segurava (o do Conforto é do
        // Conforto e não é tocado; o reservado não pode ser liberado).
        seats = seats.map((s) => (s.id === nextMid.id ? { ...s, available: true } : s))
      }
      // Se middleReserved, o assento continua indisponível por reserva — NÃO reativar.
      continue
    }

    // Re-materializa o limite: a fileira fica com no máximo 2 lugares.
    const shouldHold = canHoldMiddle
    if (shouldHold === cap.holdsMiddle) continue

    if (shouldHold) {
      out()[rowKey] = { maxTwo: true, holdsMiddle: true }
      seats = seats.map((s) => (s.id === nextMid.id ? { ...s, available: false } : s))
    } else {
      out()[rowKey] = { maxTwo: true, holdsMiddle: false }
      // Devolve o banco do meio que o LIMITE segurava (o do Conforto é do
      // Conforto e não é tocado; o reservado não pode ser liberado).
      if (cap.holdsMiddle && !comfortOwns && !middleReserved) {
        seats = seats.map((s) => (s.id === nextMid.id ? { ...s, available: true } : s))
      }
    }
  }

  return { seats, caps: outCaps ?? caps }
}

/** Badge do passageiro: as fileiras com limite ligado. */
export interface RowCapBadge {
  row: number
  /** "Banco de trás" */
  label: string
  /** "Até dois no banco de trás" */
  fullLabel: string
}

/** Fileiras com limite ligado, da frente para trás. */
export function activeRowCapBadges(caps: RowCaps): RowCapBadge[] {
  return Object.keys(caps)
    .filter((key) => caps[key]?.maxTwo === true)
    .map((key) => Number(key))
    .filter((row) => Number.isFinite(row))
    .sort((a, b) => a - b)
    .map((row) => ({
      row,
      label: rowCapShortLabel(row),
      fullLabel: rowCapLabel(row),
    }))
}

/** Frase para o passageiro ("Banco de trás: até 2 pessoas"). */
export function rowCapNotice(caps: RowCaps): string | null {
  const badges = activeRowCapBadges(caps)
  if (badges.length === 0) return null
  if (badges.length === 1) return `${badges[0].label}: até 2 pessoas.`
  return `${badges.map((b) => b.label).join(" e ")}: até 2 pessoas cada.`
}
